import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import Field from '../../components/Field.jsx';
import IdentifiersSection from '../../components/identity/IdentifiersSection.jsx';
import LogoSection from '../../components/identity/LogoSection.jsx';
import Alert from '../../components/ui/Alert.jsx';
import ConfirmModal from '../../components/ui/ConfirmModal.jsx';
import Modal, { ModalActions } from '../../components/ui/Modal.jsx';
import { EmptyState, Loading, PageHeader, Pill, Section } from '../../components/ui/index.jsx';
import { fmtDay } from '../../utils/format.js';
import { ScopeField, scopeFromKey } from '../people/peopleShared.jsx';
import { ROLE_SUGGESTIONS } from './supplierShared.js';
import { checkDateOrder, checkRequired } from '../../utils/validate.js';

function EditSupplierModal({ base, supplier, categories, onClose, onSaved }) {
  const [form, setForm] = useState({ name: supplier.name, category: supplier.category, notes: supplier.notes || '' });
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const problem = checkRequired(form.name, 'the supplier’s name', { max: 200 });
    if (problem) { setErrors({ name: problem }); return; }
    setBusy(true); setError('');
    try { await api(base, { method: 'PATCH', body: form }); onSaved(); } catch (err) {
      setErrors(err.data?.fields || {}); setError(err.data?.fields ? '' : err.message); setBusy(false);
    }
  };
  return (
    <Modal title="Edit supplier" onClose={onClose} onSubmit={save} busy={busy}
      footer={<ModalActions submitLabel="Save" busyLabel="Saving…" busy={busy} onCancel={onClose} />}>
      {error && <Alert tone="danger">{error}</Alert>}
      <Field label="Supplier name" required maxLength={200} value={form.name} onChange={(e) => { setForm({ ...form, name: e.target.value }); setErrors({}); }} error={errors.name} />
      <Field label="What they do" as="select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} error={errors.category}>
        {Object.entries(categories).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </Field>
      <Field label="Notes" optional hint="Only your team sees these." as="textarea" rows={3} maxLength={1000} counter value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} error={errors.notes} />
    </Modal>
  );
}

function AppointModal({ base, data, onClose, onSaved }) {
  const [form, setForm] = useState({ role_label: ROLE_SUGGESTIONS[data.supplier.category] || '', scope: 'company', valid_from: '', valid_until: '', is_public: true });
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k, v) => { setForm({ ...form, [k]: v }); if (errors[k]) setErrors({ ...errors, [k]: undefined }); };
  const save = async () => {
    const found = Object.fromEntries(Object.entries({
      role_label: checkRequired(form.role_label, 'what they’re official for, e.g. “Official housing bureau”', { max: 200 }),
      valid_until: checkDateOrder(form.valid_from, form.valid_until),
    }).filter(([, m]) => m));
    if (Object.keys(found).length) { setErrors(found); return; }
    setBusy(true); setError('');
    try { await api(`${base}/appointments`, { method: 'POST', body: { ...form, scope: scopeFromKey(form.scope) } }); onSaved(); }
    catch (err) { setErrors(err.data?.fields || {}); setError(err.data?.fields ? '' : err.message); setBusy(false); }
  };
  return (
    <Modal title="Make them official for…" onClose={onClose} onSubmit={save} busy={busy}
      description="Checks of their details then say they’re official, in this role, for these events and dates. Outside them, they can’t be verified."
      footer={<ModalActions submitLabel="Save" busyLabel="Saving…" busy={busy} onCancel={onClose} />}>
      {error && <Alert tone="danger">{error}</Alert>}
      <Field label="Their official role" required maxLength={200} value={form.role_label} onChange={(e) => set('role_label', e.target.value)} error={errors.role_label}
        placeholder="Official housing bureau" hint={!errors.role_label ? 'What people are told when they check, e.g. “Official housing bureau”.' : undefined} />
      <ScopeField value={form.scope} onChange={(v) => set('scope', v)} scopes={data.scopes} companyName={data.organisation.name} error={errors.scope} />
      <div className="row-2">
        <Field label="From" optional type="date" value={form.valid_from} onChange={(e) => set('valid_from', e.target.value)} error={errors.valid_from} />
        <Field label="Until" optional type="date" min={form.valid_from || undefined} value={form.valid_until} onChange={(e) => set('valid_until', e.target.value)} error={errors.valid_until} />
      </div>
      <label className="checkbox">
        <input type="checkbox" checked={form.is_public} onChange={(e) => set('is_public', e.target.checked)} />
        <span>Show them on the verify page <span className="muted small">(their role, website, booking link and phone, so people can check a call or email claiming to be them)</span></span>
      </label>
    </Modal>
  );
}

export default function Supplier() {
  const { supplierId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { organisation: current } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(location.state?.notice || '');
  const [modal, setModal] = useState(null);   // edit | appoint | remove | { end } | { toggle }
  const orgBase = current ? `/portal/organisations/${current.id}` : '';
  const base = `${orgBase}/suppliers/${supplierId}`;

  const load = useCallback(async () => {
    if (!current) return;
    try { setData(await api(base)); setError(''); } catch (err) { setError(err.status === 404 ? 'This supplier isn’t listed.' : err.message); }
  }, [base, current]);
  useEffect(() => { load(); }, [load]);

  const back = { to: '/company/suppliers', label: 'Official suppliers' };
  if (error) return <div className="page"><PageHeader back={back} title="Supplier" /><Alert tone="danger">{error}</Alert></div>;
  if (!data) return <div className="page"><Loading /></div>;
  const { supplier: s, appointments, organisation: org } = data;
  const canManage = org.can_manage;
  const done = async (text) => { setModal(null); if (text) setNotice(text); await load(); };

  return (
    <div className="page">
      <PageHeader back={back} title={s.name} meta={<span className="muted">{s.category_label}</span>}
        actions={canManage && <>
          <button type="button" className="btn btn-secondary" onClick={() => setModal('edit')}>Edit details</button>
          <button type="button" className="btn btn-danger-ghost" onClick={() => setModal('remove')}>Remove</button>
        </>} />
      {notice && <Alert tone="success">{notice}</Alert>}
      {s.notes && (
        <Section title="Notes" description="Only your team sees these.">
          <p className="notes-text">{s.notes}</p>
        </Section>
      )}

      <Section title="Official for" description="Checks of their details only say “official” for these events, between these dates."
        actions={canManage && <button type="button" className="btn btn-primary btn-small" onClick={() => setModal('appoint')}>Add role</button>}
        flush={appointments.length > 0}>
        {appointments.length === 0 ? <EmptyState>Not official for anything yet: checks of their details will say they cannot be verified.</EmptyState> : (
          <div className="table-scroll">
            <table className="table table-stack">
              <thead><tr><th>Role</th><th>Events</th><th>Dates</th><th>Verify page</th><th><span className="sr-only">Actions</span></th></tr></thead>
              <tbody>
                {appointments.map((a) => (
                  <tr key={a.id}>
                    <td data-label="Role">{a.role_label}</td>
                    <td data-label="Events">{a.covers.type === 'company' ? 'Every event' : a.covers.type === 'brand' ? `${a.covers.name} (brand)` : a.covers.name}</td>
                    <td data-label="Dates">{a.valid_from || a.valid_until ? `${a.valid_from ? fmtDay(a.valid_from) : '…'} – ${a.valid_until ? fmtDay(a.valid_until) : '…'}` : 'Any time'}</td>
                    <td data-label="Verify page">{a.is_public ? <Pill tone="info">Listed</Pill> : <Pill>Not listed</Pill>}</td>
                    <td className="right row-actions nowrap">
                      {canManage && <button type="button" className="btn btn-small btn-secondary" onClick={() => setModal({ toggle: a })}>{a.is_public ? 'Stop listing' : 'List'}</button>}
                      {canManage && <button type="button" className="btn btn-small btn-danger-ghost" onClick={() => setModal({ end: a })}>End</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <LogoSection base={orgBase} entityId={s.id} ownerName={s.name} canEdit={canManage} title="Their logo"
        description={`Shown next to ${s.name}’s details on the public verify page, where you list them. We also compare it with the images in emails people forward: an old or altered copy is a warning sign.`} />

      <IdentifiersSection
        key={data.identifiers.map((i) => i.id).join(',')}
        title="Their details"
        description="Their website, email domains, addresses, phone numbers, booking links and the bank account they take payments into. Anything not listed here is answered as “cannot be verified”."
        basePath={`${orgBase}/entities/${s.id}/identifiers`}
        ownerName={s.name}
        initial={data.identifiers}
        canEdit={canManage}
        onChange={() => load()}
        modalDescription={`Something that genuinely belongs to ${s.name}.`}
        hideVisibility
      />

      {modal === 'edit' && <EditSupplierModal base={base} supplier={s} categories={data.categories} onClose={() => setModal(null)} onSaved={() => done('Saved.')} />}
      {modal === 'appoint' && <AppointModal base={base} data={data} onClose={() => setModal(null)} onSaved={() => done('Saved.')} />}
      {modal?.toggle && (
        <ConfirmModal title={modal.toggle.is_public ? 'Stop listing them?' : 'List them on the verify page?'} confirmLabel={modal.toggle.is_public ? 'Stop listing' : 'List them'}
          onClose={() => setModal(null)}
          onConfirm={async () => { await api(`${base}/appointments/${modal.toggle.id}`, { method: 'PATCH', body: { is_public: !modal.toggle.is_public } }); await done('Saved.'); }}>
          <p>{modal.toggle.is_public
            ? 'They won’t appear in the list of official channels. Checks of their details will still say they’re official.'
            : 'Their website, booking link and phone number will appear on the verify page with their role, so people can find the real one.'}</p>
        </ConfirmModal>
      )}
      {modal?.end && (
        <ConfirmModal title={`End their role as ${modal.end.role_label}?`} confirmLabel="End role" danger onClose={() => setModal(null)}
          onConfirm={async () => { await api(`${base}/appointments/${modal.end.id}`, { method: 'DELETE' }); await done('Ended.'); }}>
          <p>They stop being official for this straight away. Checks of their details will say they cannot be verified for those events.</p>
        </ConfirmModal>
      )}
      {modal === 'remove' && (
        <ConfirmModal title={`Remove ${s.name}?`} confirmLabel="Remove for good" danger typeToConfirm={s.name} onClose={() => setModal(null)}
          onConfirm={async () => { await api(base, { method: 'DELETE' }); navigate('/company/suppliers', { replace: true }); }}>
          <p>This deletes {s.name} and their details for good. They stop being official for every event straight away and come off the verify page.</p>
          <p>If they&rsquo;ve only stopped working on one event, end that role instead.</p>
        </ConfirmModal>
      )}
    </div>
  );
}
